// src/components/AddBillForm.tsx
import {
	TextInput,
	Button,
	Paper,
	Stack,
	Group,
	Collapse,
	UnstyledButton,
	Checkbox,
	Badge,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useState, useRef, ChangeEvent, FocusEvent, MouseEvent } from 'react';
import { IconPlus, IconPuzzle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { generateBillId } from '../utils/generateId';
import { normalizeDateForStorage } from '../utils/dateUtils';
import { Bill, BillWidgetData } from '../types';
import WidgetDataModal, { useWidgetIntegrationCount } from './WidgetDataModal';

interface AddBillFormProps {
	onAddBill: (bill: Bill) => void;
}

interface FormValues {
	name: string;
	amount: string;
	dueDate: Date | null;
}

function AddBillForm({ onAddBill }: AddBillFormProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const [displayAmount, setDisplayAmount] = useState('0.00');
	const [isRecurring, setIsRecurring] = useState(false);
	const [widgetData, setWidgetData] = useState<BillWidgetData>({});
	const [widgetModalOpen, setWidgetModalOpen] = useState(false);
	const amountInputRef = useRef<HTMLInputElement>(null);
	const widgetIntegrationCount = useWidgetIntegrationCount();

	const form = useForm<FormValues>({
		initialValues: {
			name: '',
			amount: '0.00',
			dueDate: null,
		},
		validate: {
			name: (value) =>
				!value || value.length < 2 ? 'Please enter a bill name' : null,
			amount: (value) =>
				!value || parseFloat(value) <= 0
					? 'Please enter a valid amount'
					: null,
			dueDate: (value) => {
				if (!value) return 'Please select a due date';
				if (isRecurring) {
					const day = value.getDate();
					if (day > 28) {
						return 'Recurring bills must be due on or before the 28th of the month';
					}
				}
				return null;
			},
		},
	});

	const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
		let numbers = event.target.value.replace(/\D/g, '');
		numbers = numbers.slice(-10);

		if (numbers.length === 0) {
			setDisplayAmount('0.00');
			form.setFieldValue('amount', '0.00');
			return;
		}

		const decimal = (parseInt(numbers) / 100).toFixed(2);
		setDisplayAmount(decimal);
		form.setFieldValue('amount', decimal);

		requestAnimationFrame(() => {
			if (amountInputRef.current) {
				const length = amountInputRef.current.value.length;
				amountInputRef.current.setSelectionRange(length, length);
			}
		});
	};

	const handleAmountFocus = (event: FocusEvent<HTMLInputElement>) => {
		const length = event.target.value.length;
		event.target.setSelectionRange(length, length);
	};

	const handleAmountClick = (event: MouseEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		const length = target.value.length;
		target.setSelectionRange(length, length);
	};

	const handleDateChange = (date: Date | null) => {
		form.setFieldValue('dueDate', date);

		if (isRecurring && date) {
			const day = date.getDate();
			if (day > 28) {
				notifications.show({
					title: 'Invalid Recurring Date',
					message:
						'Recurring bills must be due on or before the 28th of the month.',
					color: 'red',
				});
			}
		}
	};

	const handleRecurringChange = (event: ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.currentTarget.checked;
		setIsRecurring(isChecked);

		if (isChecked && form.values.dueDate) {
			const day = form.values.dueDate.getDate();
			if (day > 28) {
				notifications.show({
					title: 'Invalid Recurring Date',
					message:
						'Please select a date on or before the 28th of the month for recurring bills.',
					color: 'red',
				});
				form.setFieldValue('dueDate', null);
			}
		}
	};

	const handleSubmit = (values: FormValues) => {
		if (isRecurring && values.dueDate) {
			const day = values.dueDate.getDate();
			if (day > 28) {
				notifications.show({
					title: 'Invalid Recurring Date',
					message:
						'Recurring bills must be due on or before the 28th of the month.',
					color: 'red',
				});
				return;
			}
		}

		const newBill: Bill = {
			id: generateBillId(),
			name: values.name.trim(),
			amount: parseFloat(values.amount),
			dueDate: normalizeDateForStorage(values.dueDate),
			recurring: isRecurring,
			paid: false,
		};

		// Only include widgetData if there's data
		if (Object.keys(widgetData).length > 0) {
			newBill.widgetData = widgetData;
		}

		onAddBill(newBill);

		form.reset();
		setDisplayAmount('0.00');
		setIsRecurring(false);
		setWidgetData({});
		setWidgetModalOpen(false);
		setIsExpanded(false);
	};

	return (
		<Paper p={0} radius='md' withBorder>
			<Group justify='space-between' p='md'>
				{!isExpanded ? (
					<UnstyledButton
						onClick={() => setIsExpanded(true)}
						style={{ width: '100%' }}
					>
						<Group justify='flex-start' gap='xs'>
							<IconPlus
								size={20}
								style={{
									transition: 'transform 300ms ease',
									transform: isExpanded
										? 'rotate(45deg)'
										: 'none',
								}}
							/>
							<span>Add Bill</span>
						</Group>
					</UnstyledButton>
				) : (
					<>
						<UnstyledButton
							onClick={() => setIsExpanded(false)}
							style={{
								transition: 'background-color 150ms ease',
								display: 'flex',
								alignItems: 'center',
								gap: 'var(--mantine-spacing-xs)',
							}}
						>
							<IconPlus
								size={20}
								style={{
									transition: 'transform 200ms ease',
									transform: 'rotate(45deg)',
								}}
							/>
						</UnstyledButton>

						<Checkbox
							label='Recurring Monthly'
							checked={isRecurring}
							onChange={handleRecurringChange}
							styles={{
								root: {
									marginBottom: 0,
								},
								label: {
									whiteSpace: 'nowrap',
								},
							}}
						/>
					</>
				)}
			</Group>

			<Collapse in={isExpanded} transitionDuration={200}>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack gap='md' p='md' pt={0}>
						<TextInput
							placeholder='Enter bill name'
							label='Bill Name'
							size='md'
							{...form.getInputProps('name')}
						/>
						<Group grow align='flex-start'>
							<TextInput
								ref={amountInputRef}
								type='text'
								inputMode='numeric'
								placeholder='0.00'
								label='Amount'
								size='md'
								value={`$${displayAmount}`}
								onChange={handleAmountChange}
								onFocus={handleAmountFocus}
								onClick={handleAmountClick}
								error={form.errors.amount}
								styles={{
									input: {
										textAlign: 'right',
										direction: 'ltr',
										fontFeatureSettings: 'tnum',
										caretColor: 'transparent',
									},
								}}
							/>
							<DatePickerInput
								clearable
								label='Due Date'
								placeholder='Select'
								size='md'
								value={form.values.dueDate}
								onChange={handleDateChange}
								error={form.errors.dueDate}
								valueFormat='M/D'
								dropdownType='modal'
								modalProps={{ centered: true }}
							/>
						</Group>
						<Group justify="space-between">
							{widgetIntegrationCount > 0 && (
								<Button
									variant="light"
									size="sm"
									leftSection={<IconPuzzle size={16} />}
									rightSection={
										Object.keys(widgetData).length > 0 ? (
											<Badge size="xs" variant="filled" circle>
												{Object.keys(widgetData).length}
											</Badge>
										) : null
									}
									onClick={() => setWidgetModalOpen(true)}
								>
									Widgets
								</Button>
							)}
							<Button type='submit' size='md' style={{ marginLeft: 'auto' }}>
								Add Bill
							</Button>
						</Group>

						<WidgetDataModal
							opened={widgetModalOpen}
							onClose={() => setWidgetModalOpen(false)}
							existingWidgetData={widgetData}
							onSave={setWidgetData}
							isNewBill={true}
							billName={form.values.name}
						/>
					</Stack>
				</form>
			</Collapse>
		</Paper>
	);
}

export default AddBillForm;
